document.documentElement.classList.remove('no-js');

class HouseFloatingPlayer {
  constructor(root) {
    this.root = root;
    this.audio = root.querySelector('[data-player-audio]');
    this.playlistNode = root.querySelector('[data-player-playlist]');
    this.playButton = root.querySelector('[data-player-play]');
    this.muteButton = root.querySelector('[data-player-mute]');
    this.progress = root.querySelector('[data-player-progress]');
    this.notice = root.querySelector('[data-player-notice]');
    this.storageKey = 'house-of-bridie-floating-player-v1';
    this.autoplay = root.dataset.autoplay === 'true';
    this.loop = root.dataset.loop === 'true';
    this.remember = root.dataset.remember === 'true';
    this.defaultVolume = Number(root.dataset.volume || 0.55);
    this.index = 0;
    this.pendingTime = 0;
    this.lastSavedAt = 0;
    this.userPaused = false;
    this.unlockHandler = null;

    try {
      this.tracks = JSON.parse(this.playlistNode.textContent);
    } catch (error) {
      this.tracks = [];
    }

    if (!this.audio || this.tracks.length === 0) return;

    this.restore();
    this.bind();
    this.loadTrack(this.index, this.pendingTime);

    const saved = this.savedState;
    const shouldPlay = saved ? (saved.playing || (this.autoplay && saved.userPaused === false)) : this.autoplay;
    if (shouldPlay) this.requestPlayback(true);
  }

  bind() {
    this.playButton?.addEventListener('click', () => {
      if (this.audio.paused) {
        this.userPaused = false;
        this.requestPlayback(false);
      } else {
        this.userPaused = true;
        this.audio.pause();
      }
    });

    this.root.querySelector('[data-player-previous]')?.addEventListener('click', () => {
      if (this.audio.currentTime > 3) {
        this.audio.currentTime = 0;
        return;
      }
      this.changeTrack(this.index - 1, !this.audio.paused);
    });

    this.root.querySelector('[data-player-next]')?.addEventListener('click', () => {
      this.changeTrack(this.index + 1, !this.audio.paused);
    });

    this.muteButton?.addEventListener('click', () => {
      this.audio.muted = !this.audio.muted;
      this.updateMuteButton();
      this.save();
    });

    this.root.querySelector('[data-player-collapse]')?.addEventListener('click', (event) => {
      const collapsed = this.root.classList.toggle('is-collapsed');
      event.currentTarget.setAttribute('aria-expanded', String(!collapsed));
      event.currentTarget.setAttribute('aria-label', collapsed ? 'Expand music player' : 'Collapse music player');
      this.save();
    });

    this.progress?.addEventListener('input', () => {
      if (!Number.isFinite(this.audio.duration)) return;
      this.audio.currentTime = (Number(this.progress.value) / 100) * this.audio.duration;
    });

    this.audio.addEventListener('loadedmetadata', () => {
      if (this.pendingTime > 0 && Number.isFinite(this.audio.duration)) {
        this.audio.currentTime = Math.min(this.pendingTime, Math.max(0, this.audio.duration - 0.25));
      }
      this.pendingTime = 0;
      this.updateTimeline();
    });
    this.audio.addEventListener('timeupdate', () => {
      this.updateTimeline();
      if (Date.now() - this.lastSavedAt > 2000) this.save();
    });
    this.audio.addEventListener('play', () => {
      this.root.classList.add('is-playing');
      this.playButton?.setAttribute('aria-label', 'Pause');
      this.clearBlocked();
      this.save();
    });
    this.audio.addEventListener('pause', () => {
      this.root.classList.remove('is-playing');
      this.playButton?.setAttribute('aria-label', 'Play');
      this.save();
    });
    this.audio.addEventListener('ended', () => {
      if (this.index < this.tracks.length - 1) {
        this.changeTrack(this.index + 1, true);
      } else if (this.loop) {
        this.changeTrack(0, true);
      } else {
        this.userPaused = true;
        this.save();
      }
    });
    this.audio.addEventListener('volumechange', () => this.updateMuteButton());
    window.addEventListener('pagehide', () => this.save());
  }

  restore() {
    let saved = null;
    if (this.remember) {
      try {
        saved = JSON.parse(sessionStorage.getItem(this.storageKey));
      } catch (error) {
        saved = null;
      }
    }

    this.savedState = saved;
    if (saved) {
      const savedIndex = this.tracks.findIndex((track) => track.src === saved.src);
      this.index = savedIndex >= 0 ? savedIndex : Math.min(Number(saved.index || 0), this.tracks.length - 1);
      this.pendingTime = Number(saved.time || 0);
      this.audio.volume = Number.isFinite(saved.volume) ? saved.volume : this.defaultVolume;
      this.audio.muted = Boolean(saved.muted);
      this.userPaused = typeof saved.userPaused === 'boolean' ? saved.userPaused : saved.playing === false;
      if (typeof saved.collapsed === 'boolean') {
        this.root.classList.toggle('is-collapsed', saved.collapsed);
        const collapse = this.root.querySelector('[data-player-collapse]');
        collapse?.setAttribute('aria-expanded', String(!saved.collapsed));
        collapse?.setAttribute('aria-label', saved.collapsed ? 'Expand music player' : 'Collapse music player');
      }
    } else {
      this.audio.volume = this.defaultVolume;
    }
    this.updateMuteButton();
  }

  loadTrack(index, time = 0) {
    this.index = ((index % this.tracks.length) + this.tracks.length) % this.tracks.length;
    const track = this.tracks[this.index];
    this.pendingTime = Number(time || 0);
    this.audio.src = track.src;
    this.audio.load();

    const title = this.root.querySelector('[data-player-title]');
    const artist = this.root.querySelector('[data-player-artist]');
    const count = this.root.querySelector('[data-player-count]');
    const cover = this.root.querySelector('[data-player-cover]');
    if (title) title.textContent = track.title;
    if (artist) artist.textContent = track.artist;
    if (count) count.textContent = `${this.index + 1} / ${this.tracks.length}`;
    if (cover) {
      if (track.cover) {
        cover.src = track.cover;
        cover.alt = `${track.title} artwork`;
        cover.hidden = false;
      } else {
        cover.removeAttribute('src');
        cover.hidden = true;
      }
    }

    if ('mediaSession' in navigator && 'MediaMetadata' in window) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: track.title,
        artist: track.artist,
        album: 'House of Bridie Listening Room',
        artwork: track.cover ? [{ src: track.cover, sizes: '240x240' }] : []
      });
    }
    this.updateTimeline();
    this.save();
  }

  changeTrack(index, continuePlaying) {
    this.loadTrack(index, 0);
    if (continuePlaying) this.requestPlayback(false);
  }

  requestPlayback(automatic) {
    const promise = this.audio.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(() => {
        this.root.classList.remove('is-playing');
        this.showBlocked();
        if (automatic) this.armPlaybackOnInteraction();
      });
    }
  }

  armPlaybackOnInteraction() {
    if (this.unlockHandler) return;
    this.unlockHandler = () => {
      this.disarmPlaybackOnInteraction();
      if (!this.userPaused) this.requestPlayback(false);
    };
    document.addEventListener('pointerdown', this.unlockHandler, { capture: true, once: true });
    document.addEventListener('keydown', this.unlockHandler, { capture: true, once: true });
  }

  disarmPlaybackOnInteraction() {
    if (!this.unlockHandler) return;
    document.removeEventListener('pointerdown', this.unlockHandler, { capture: true });
    document.removeEventListener('keydown', this.unlockHandler, { capture: true });
    this.unlockHandler = null;
  }

  showBlocked() {
    this.root.classList.add('is-awaiting-interaction');
    if (this.notice) this.notice.hidden = false;
  }

  clearBlocked() {
    this.root.classList.remove('is-awaiting-interaction');
    if (this.notice) this.notice.hidden = true;
    this.disarmPlaybackOnInteraction();
  }

  updateTimeline() {
    const current = Number(this.audio.currentTime || 0);
    const duration = Number.isFinite(this.audio.duration) ? this.audio.duration : 0;
    if (this.progress) this.progress.value = duration > 0 ? String((current / duration) * 100) : '0';
    const currentLabel = this.root.querySelector('[data-player-current]');
    const durationLabel = this.root.querySelector('[data-player-duration]');
    if (currentLabel) currentLabel.textContent = this.formatTime(current);
    if (durationLabel) durationLabel.textContent = this.formatTime(duration);
  }

  updateMuteButton() {
    if (!this.muteButton) return;
    const muted = this.audio.muted || this.audio.volume === 0;
    this.root.classList.toggle('is-muted', muted);
    this.muteButton.setAttribute('aria-pressed', String(muted));
    this.muteButton.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
  }

  save() {
    if (!this.remember || !this.audio || this.tracks.length === 0) return;
    const state = {
      index: this.index,
      src: this.tracks[this.index].src,
      time: Number(this.audio.currentTime || 0),
      playing: !this.audio.paused && !this.audio.ended,
      userPaused: this.userPaused,
      muted: this.audio.muted,
      volume: this.audio.volume,
      collapsed: this.root.classList.contains('is-collapsed')
    };
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(state));
      this.lastSavedAt = Date.now();
    } catch (error) {
      // Playback continues even when session storage is unavailable.
    }
  }

  formatTime(seconds) {
    if (!Number.isFinite(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainder = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${minutes}:${remainder}`;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const toggle = document.querySelector('[data-menu-toggle]');
  const drawer = document.querySelector('[data-mobile-drawer]');

  if (toggle && drawer) {
    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      drawer.classList.toggle('is-open', !open);
      document.body.classList.toggle('menu-open', !open);
    });
  }

  document.querySelectorAll('[data-product-form]').forEach((form) => {
    const select = form.querySelector('[data-variant-select]');
    const button = form.querySelector('[data-add-to-cart]');
    const price = document.querySelector('[data-product-price]');
    if (!select || !button) return;

    select.addEventListener('change', () => {
      const option = select.options[select.selectedIndex];
      button.disabled = option.dataset.available !== 'true';
      button.textContent = option.dataset.available === 'true' ? button.dataset.addLabel : button.dataset.soldLabel;
      if (price && option.dataset.price) price.textContent = option.dataset.price;
    });
  });

  document.querySelectorAll('[data-floating-player]').forEach((player) => {
    new HouseFloatingPlayer(player);
  });
});
